import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Badge, Empty, Table, Td } from "../components/UI";
import { SlidePanel, ConfirmDialog, ReadOnlyChip } from "../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea, SelectWithCustom } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { usePrograms } from "../lib/usePrograms";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Plus, Search, Pencil, Trash2, NotebookPen, Users, Check, X, Eye } from "lucide-react";
import { usePagination, PaginationBar } from "../components/Pagination";
import { toast } from "sonner";

const GROUP_TYPES = ["Psychoeducation", "Process Group", "Skills Training", "Support Group", "Family Group", "Adjunct Therapy"];
const CPT_CODES = ["H0004", "H0005", "H0015", "H2019", "H2032", "T1006", "90853"];
const ATT_CODES = [["S", "Show"], ["A", "Absent"], ["M", "Makeup"], ["C", "Cancelled"], ["NS", "No Show"]];
const EMPTY = { name: "", group_type: "", curriculum: "", date: dayjs().format("YYYY-MM-DD"), start_time: "09:00", end_time: "10:30", total_time: "", units: "", program: "", cpt_code: "", location: "", location_type: "agency", room: "", facilitator: "", co_facilitator: "", clients: [], objective: "", intervention: "", plan: "", responses: {}, status: "scheduled" };

function GroupForm({ form, setForm, clients, onSave, onCancel, busy, isEdit }) {
  const PROGRAMS = usePrograms();
  const [tab, setTab] = useState("session");
  const [respClient, setRespClient] = useState("");
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const inRoster = (id) => form.clients.some((c) => c.id === id);
  const toggleClient = (c) => setForm((s) => ({ ...s, clients: inRoster(c.id) ? s.clients.filter((x) => x.id !== c.id) : [...s.clients, { id: c.id, name: `${c.first_name} ${c.last_name}`, phone: c.phone || "" }] }));
  const resp = form.responses[respClient] || {};
  const setResp = (k) => (e) => setForm((s) => ({ ...s, responses: { ...s.responses, [respClient]: { ...(s.responses[respClient] || {}), [k]: e.target.value } } }));

  return (
    <form onSubmit={onSave} className="space-y-4">
      <div className="flex gap-2">
        {[["session", "Group Session Information"], ["irp", "Intervention / Response / Plan"]].map(([k, l]) => (
          <button key={k} type="button" data-testid={`group-form-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${tab === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{l}</button>
        ))}
      </div>
      {tab === "session" ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
            <div className="col-span-2"><FLabel>Group Name *</FLabel><FInput data-testid="group-name-input" value={form.name} onChange={f("name")} required /></div>
            <div><FLabel>Group Type</FLabel><FSelect data-testid="group-type-select" value={form.group_type} onChange={f("group_type")}>{GROUP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</FSelect></div>
            <div><FLabel>Curriculum</FLabel><FInput value={form.curriculum} onChange={f("curriculum")} /></div>
            <div><FLabel>Date *</FLabel><DateField testId="group-date" value={form.date} onChange={(v) => setForm((s) => ({ ...s, date: v }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Start</FLabel><FInput type="time" value={form.start_time} onChange={f("start_time")} /></div>
              <div><FLabel>End</FLabel><FInput type="time" value={form.end_time} onChange={f("end_time")} /></div>
            </div>
            <div><FLabel>Units</FLabel><FInput value={form.units} onChange={f("units")} /></div>
            <div><FLabel>Program</FLabel><FSelect data-testid="group-program-select" value={form.program} onChange={f("program")}>{PROGRAMS.map((p) => <option key={p} value={p}>{p}</option>)}</FSelect></div>
            <div><FLabel>CPT Code</FLabel><SelectWithCustom testId="group-cpt-select" value={form.cpt_code} onChange={(v) => setForm((s) => ({ ...s, cpt_code: v }))} options={CPT_CODES} placeholder="Select here" /></div>
            <div><FLabel>Location</FLabel><FInput value={form.location} onChange={f("location")} /></div>
            <div><FLabel>Room</FLabel><FInput value={form.room} onChange={f("room")} /></div>
            <div><FLabel>Facilitator</FLabel><FInput data-testid="group-facilitator-input" value={form.facilitator} onChange={f("facilitator")} /></div>
            <div><FLabel>Co-Facilitator</FLabel><FInput value={form.co_facilitator} onChange={f("co_facilitator")} /></div>
            <div><FLabel>Status</FLabel><FSelect data-testid="group-status-select" value={form.status} onChange={f("status")}>{[["scheduled", "Scheduled"], ["completed", "Completed"], ["tbd", "To be Determined"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</FSelect></div>
          </div>
          <div>
            <FLabel>Location Type</FLabel>
            <div className="flex gap-4 mt-1">
              {[["agency", "Agency"], ["community", "Community"], ["telehealth", "Telehealth"]].map(([v, l]) => (
                <label key={v} className="flex items-center gap-2 text-sm text-[#4E342E] cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-[#4E342E]" checked={form.location_type === v} onChange={() => setForm((s) => ({ ...s, location_type: v }))} /> {l}
                </label>
              ))}
            </div>
          </div>
          <div>
            <FLabel>Roster - Add Clients ({form.clients.length})</FLabel>
            <div className="mt-1 max-h-44 overflow-y-auto rounded-xl border border-[#E5DFD3] p-2 grid grid-cols-1 md:grid-cols-2 gap-1" data-testid="group-roster">
              {clients.map((c) => (
                <label key={c.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-[#F5F2EB] cursor-pointer">
                  <input type="checkbox" data-testid={`roster-client-${c.id}`} className="h-4 w-4 accent-[#4E342E]" checked={inRoster(c.id)} onChange={() => toggleClient(c)} />
                  <span className="text-[#3E2723]">{c.first_name} {c.last_name}</span>
                  <span className="ml-auto text-xs text-[#84675B]">{c.phone}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div><FLabel>Objective</FLabel><FTextarea data-testid="group-objective-input" rows={2} value={form.objective} onChange={f("objective")} /></div>
          <div><FLabel>Intervention</FLabel><FTextarea data-testid="group-intervention-input" rows={2} value={form.intervention} onChange={f("intervention")} /></div>
          <div><FLabel>Plan</FLabel><FTextarea rows={2} value={form.plan} onChange={f("plan")} /></div>
          <div className="rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] p-4">
            <FLabel>Per-Client Response</FLabel>
            <FSelect data-testid="response-client-select" className="mt-1" value={respClient} onChange={(e) => setRespClient(e.target.value)}>
              {form.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </FSelect>
            {respClient && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><FLabel>Intervention</FLabel><FInput value={resp.intervention || ""} onChange={setResp("intervention")} /></div>
                <div><FLabel>Level of Participation</FLabel>
                  <FSelect value={resp.participation || ""} onChange={setResp("participation")}>{["Active", "Moderate", "Minimal", "Resistant"].map((p) => <option key={p} value={p}>{p}</option>)}</FSelect></div>
                <div className="md:col-span-2"><FLabel>Response</FLabel><FTextarea rows={2} value={resp.response || ""} onChange={setResp("response")} /></div>
                <div><FLabel>Progress</FLabel><FSelect value={resp.progress || ""} onChange={setResp("progress")}>{["Improving", "Stable", "Regressing", "Needs follow-up"].map((p) => <option key={p} value={p}>{p}</option>)}</FSelect></div>
                <div><FLabel>Plan</FLabel><FInput value={resp.plan || ""} onChange={setResp("plan")} /></div>
              </div>
            )}
          </div>
        </>
      )}
      <div className="sticky bottom-0 -mx-6 -mb-5 mt-4 flex justify-end gap-2 border-t border-[#F1EEE7] bg-white px-6 py-4">
        <Btn type="button" variant="ghost" onClick={onCancel}>Back</Btn>
        <Btn data-testid="group-save-btn" type="submit" disabled={busy || !form.name.trim()}>{busy ? "Saving…" : isEdit ? "Save Changes" : "Create Group"}</Btn>
      </div>
    </form>
  );
}

function Attendance({ groups, reload, canWrite }) {
  const [groupId, setGroupId] = useState("");
  const [att, setAtt] = useState({});
  const g = groups.find((x) => x.id === groupId);
  useEffect(() => { if (g) setAtt(g.attendance || {}); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (payload) => {
    try {
      const r = await api.put(`/groups/${groupId}/attendance`, payload);
      setAtt(r.data.attendance || {});
      toast.success("Attendance saved");
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Card className="p-6" data-testid="attendance-panel">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-96 max-w-full"><FLabel>Group Session</FLabel>
          <FSelect data-testid="attendance-group-select" value={groupId} onChange={(e) => setGroupId(e.target.value)} placeholderDisabled>
            {groups.map((x) => <option key={x.id} value={x.id}>{dayjs(x.date).format("MM/DD")} - {x.name} ({x.start_time})</option>)}
          </FSelect></div>
        {g && canWrite && (
          <div className="flex gap-2">
            <Btn data-testid="make-all-present" variant="outline" className="rounded-lg text-xs" onClick={() => save({ all: "S" })}><Check size={13} /> Make all Present</Btn>
            <Btn data-testid="make-all-absent" variant="outline" className="rounded-lg text-xs" onClick={() => save({ all: "A" })}><X size={13} /> Make all Absent</Btn>
          </div>
        )}
      </div>
      {!g ? <Empty text="Select a group session to take attendance" /> : (
        <>
          <Table headers={["Client", "Phone", "Start", "End", ...ATT_CODES.map(([c, l]) => `${c} (${l})`)]}>
            {(g.clients || []).map((c) => (
              <tr key={c.id} data-testid={`att-row-${c.id}`}>
                <Td className="font-semibold">{c.name}</Td>
                <Td>{c.phone || "-"}</Td>
                <Td>{g.start_time}</Td>
                <Td>{g.end_time}</Td>
                {ATT_CODES.map(([code]) => (
                  <Td key={code}>
                    <input type="checkbox" data-testid={`att-${c.id}-${code}`} className="h-4 w-4 accent-[#4E342E]" disabled={!canWrite}
                      checked={att[c.id] === code}
                      onChange={() => setAtt((a) => ({ ...a, [c.id]: a[c.id] === code ? "" : code }))} />
                  </Td>
                ))}
              </tr>
            ))}
          </Table>
          {canWrite && <div className="mt-4 flex justify-end"><Btn data-testid="save-attendance-btn" onClick={() => save({ attendance: att })}>Save Attendance</Btn></div>}
        </>
      )}
    </Card>
  );
}

function InterventionLibrary({ canWrite }) {
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState({ group_name: "", group_type: "", objective: "", intervention: "" });
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/interventions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/interventions/${editId}`, form);
      else await api.post("/interventions", form);
      toast.success("Intervention saved");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const toggleActive = async (i) => { await api.put(`/interventions/${i.id}`, { active: !i.active }); load(); };
  return (
    <>
      {canWrite && <div className="flex justify-end mb-3"><Btn data-testid="add-intervention-btn" onClick={() => { setForm({ group_name: "", group_type: "", objective: "", intervention: "" }); setEditId(null); setPanel(true); }}><Plus size={15} /> New Intervention</Btn></div>}
      <Card>
        <Table headers={["Group Name", "Type", "Objective", "Intervention", "Creator", "Status", ""]}>
          {items.map((i) => (
            <tr key={i.id} data-testid={`intervention-row-${i.id}`}>
              <Td className="font-semibold">{i.group_name}</Td>
              <Td>{i.group_type}</Td>
              <Td className="max-w-[240px] truncate">{i.objective}</Td>
              <Td className="max-w-[260px] truncate">{i.intervention}</Td>
              <Td>{i.creator}</Td>
              <Td>
                <button type="button" data-testid={`intervention-toggle-${i.id}`} disabled={!canWrite} onClick={() => toggleActive(i)}
                  className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${i.active ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#F5F2EB] text-[#7B6157]"}`}>
                  {i.active ? "Activated" : "Deactivated"}
                </button>
              </Td>
              <Td>{canWrite && (
                <button type="button" data-testid={`intervention-edit-${i.id}`} aria-label="Edit" onClick={() => { setForm({ group_name: i.group_name, group_type: i.group_type, objective: i.objective, intervention: i.intervention }); setEditId(i.id); setPanel(true); }}
                  className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>)}
              </Td>
            </tr>
          ))}
        </Table>
        {items.length === 0 && <Empty text="No interventions in the library" />}
      </Card>
      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Intervention" : "New Intervention"} subtitle="Reusable objectives & interventions for group sessions" testId="intervention-slide-panel">
        <form onSubmit={save} className="space-y-4">
          <div><FLabel>Group Name *</FLabel><FInput data-testid="intervention-name-input" value={form.group_name} onChange={(e) => setForm((s) => ({ ...s, group_name: e.target.value }))} required /></div>
          <div><FLabel>Group Type</FLabel><FSelect value={form.group_type} onChange={(e) => setForm((s) => ({ ...s, group_type: e.target.value }))}>{GROUP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</FSelect></div>
          <div><FLabel>Objective</FLabel><FTextarea rows={2} value={form.objective} onChange={(e) => setForm((s) => ({ ...s, objective: e.target.value }))} /></div>
          <div><FLabel>Intervention</FLabel><FTextarea rows={3} value={form.intervention} onChange={(e) => setForm((s) => ({ ...s, intervention: e.target.value }))} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="intervention-save-btn" type="submit">Save</Btn>
          </div>
        </form>
      </SlidePanel>
    </>
  );
}

export default function Groups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = canEdit(user, "clinical") || user?.role === "admin";
  const [tab, setTab] = useState("all");
  const [groups, setGroups] = useState([]);
  const groupsPg = usePagination(groups);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/groups", { params: search ? { q: search } : {} }).then((r) => setGroups(r.data));
  useEffect(() => { load(); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/clients", { params: { status: "active" } }).then((r) => setClients(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/groups/${editId}`, form);
      else await api.post("/groups", form);
      toast.success(editId ? "Group updated" : "Group created");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const openEdit = (g) => { setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, g[k] ?? EMPTY[k]])) }); setEditId(g.id); setPanel(true); };
  const archive = async () => {
    await api.delete(`/groups/${confirm.id}`);
    toast.success(`"${confirm.name}" archived`);
    setConfirm(null); load();
  };

  return (
    <div>
      <PageHeader title="Groups" subtitle="Group therapy sessions, attendance & intervention library"
        action={canWrite ? <Btn data-testid="create-group-btn" onClick={() => { setForm(EMPTY); setEditId(null); setPanel(true); }}><Plus size={16} /> Create New Group</Btn> : <ReadOnlyChip />} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {[["all", "All Groups"], ["attendance", "Daily Attendance"], ["library", "Intervention Library"]].map(([k, l]) => (
            <button key={k} data-testid={`groups-tab-${k}`} onClick={() => setTab(k)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{l}</button>
          ))}
        </div>
        {tab === "all" && (
          <div className="relative ml-auto">
            <Search size={15} className="absolute left-3 top-3 text-[#84675B]" />
            <FInput data-testid="groups-search" placeholder="Search groups…" className="pl-9 h-10 w-60" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        )}
      </div>

      {tab === "all" && (
        <Card>
          <Table headers={["Date", "Group Name", "Type", "Facilitator", "Clients", "Location", "Program", "CPT", "Time", "Status", ""]}>
            {groupsPg.pageRows.map((g) => (
              <tr key={g.id} data-testid={`group-row-${g.id}`} className="hover:bg-[#FDFCF8]">
                <Td className="tabular-nums whitespace-nowrap">{dayjs(g.date).format("MM/DD/YYYY")}</Td>
                <Td className="font-semibold max-w-[200px] truncate">
                  <button data-testid={`group-name-link-${g.id}`} onClick={() => navigate(`/group360/${g.id}`)} className="truncate text-left hover:underline text-[#4E342E]">{g.name}</button>
                </Td>
                <Td className="whitespace-nowrap">{g.group_type}</Td>
                <Td className="whitespace-nowrap">{g.facilitator}</Td>
                <Td><span className="inline-flex items-center gap-1"><Users size={13} className="text-[#7B6157]" /> {(g.clients || []).length}</span></Td>
                <Td className="whitespace-nowrap">{g.location}{g.location_type === "telehealth" ? " (TH)" : ""}</Td>
                <Td className="max-w-[140px] truncate">{g.program}</Td>
                <Td>{g.cpt_code}</Td>
                <Td className="whitespace-nowrap">{g.start_time} to {g.end_time}</Td>
                <Td><Badge value={g.status === "tbd" ? "tbd" : g.status} /></Td>
                <Td>
                  <div className="flex gap-1.5 justify-end">
                    <button data-testid={`group-360-${g.id}`} title="Group 360° view" onClick={() => navigate(`/group360/${g.id}`)}
                      className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Eye size={13} /></button>
                    <button data-testid={`group-note-${g.id}`} title="Group note (Intervention/Response/Plan)" onClick={() => { openEdit(g); }}
                      className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><NotebookPen size={13} /></button>
                    {canWrite && <>
                      <button data-testid={`group-edit-${g.id}`} title="Edit" onClick={() => openEdit(g)}
                        className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                      <button data-testid={`group-archive-${g.id}`} title="Archive" onClick={() => setConfirm(g)}
                        className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                    </>}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
          <PaginationBar {...groupsPg} testId="groups-pagination" />
          {groups.length === 0 && <Empty text="No groups yet - create your first group session" />}
        </Card>
      )}
      {tab === "attendance" && <Attendance groups={groups} reload={load} canWrite={canWrite} />}
      {tab === "library" && <InterventionLibrary canWrite={canWrite} />}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Group Session" : "Create New Group"}
        subtitle="Session details, roster & intervention/response/plan" width="max-w-3xl" testId="group-slide-panel">
        <GroupForm form={form} setForm={setForm} clients={clients} onSave={save} onCancel={() => setPanel(false)} busy={busy} isEdit={!!editId} />
      </SlidePanel>

      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={archive}
        title={`Archive "${confirm?.name}"`} confirmWord="ARCHIVE" confirmLabel="Archive"
        warning="Archived groups are removed from lists and the scheduler but keep their attendance history." />
    </div>
  );
}
